import { gql, useQuery } from '@apollo/client';
import type { QueryResult as ApolloQueryResult } from '@apollo/client';
import { Exception } from '@livecontrol/core-ui';
import { Obj } from '@livecontrol/core-utils';
import { Account } from '@livecontrol/scheduler/model';
import { useEffect, useState } from 'react';
import type { QueryResult } from '../graphql';
import * as Dbx from './dbx';

interface TVariables {
  clientId: number;
}

interface TData {
  getLocationsAirtableData?: {
    id: string
    secondary_id?: number,
    fields: any
  }[];
}

interface AirtableLocation {
  id: string
  secondary_id: number,
  fields: any
}

export const QUERY = gql`
  query GetLocationsAirtableData($clientId: Float!) {
    getLocationsAirtableData(clientId: $clientId) {
      id
      secondary_id
      fields
    }
  }
`;

export const useLocationsAirtable = (
  args: Account.Like
): Partial<Pick<ApolloQueryResult, 'refetch'>> & QueryResult<AirtableLocation[], 'airtableLocations'> => {
  const [error, setError] = useState<Error | undefined>();
  const [airtableLocations, setLocations] = useState<AirtableLocation[] | undefined>();

  // Parse the input arguments
  const variables = {
    clientId: Account.toId(args)
  };

  // Validate the input
  if (!Obj.isHydrated(variables)) {
    return { loading: false, error: Exception.KABOOM };
  }

  // Prepare the query
  const { loading, data, refetch } = useQuery<TData, TVariables>(QUERY, {
    variables,
    onError() {
      setError(Exception.KABOOM);
    }
  });

  // When available, parse server response
  useEffect(() => {
    let locations_;
    let error_;

    if (data) {
      locations_ = (data.getLocationsAirtableData ?? [])
        .map((record: Dbx.RecordAirtableLocation) => {
          return Dbx.normalizeATLocation(record)
        })
        .filter((µ: AirtableLocation | undefined): µ is AirtableLocation => !!µ)
    }

    setLocations(locations_);
    setError(error_);
  }, [data]);

  return { loading, error, airtableLocations, refetch };
};
